import { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n, useT } from '../lib/i18n';
import { calendarRows } from '../lib/calendarRows';
import { fitPanel } from '../lib/fitPanel';
import {
  addMonths, formatters, isSameMonth, monthGrid, startOfMonth, type Day,
} from '../lib/dates';

/**
 * Arrival and departure, the way every guest has already learned them.
 *
 * ---------------------------------------------------------------------------
 * WHY TWO FIELDS AND NOT A LENGTH
 * ---------------------------------------------------------------------------
 *
 * This replaced a picker that asked for a LENGTH first and then showed a
 * calendar of arrivals. That order was not a design conviction; it was a
 * request budget. `/api/public/availability` answers one arrival and one length
 * at a time, so a month view cost up to 42 calls and could only be afforded
 * once a length was known. `/api/public/calendar` sends the compressed periods
 * for a whole window in one request, and the constraint is gone.
 *
 * What is left is the question of which order is better for a guest, and it is
 * not close. Every OTA and the park's own previous calculator take two dates.
 * People think in dates — "we are away the week of 25 July" — not in nights.
 * And the two-field flow is strictly MORE accurate: instead of filtering
 * arrivals by a length the guest guessed, it filters departures by an arrival
 * they actually chose, so it can say "three to six nights from here, but not
 * seven" where a length-first calendar could only refuse the whole date.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS GREYED, AND WHEN
 * ---------------------------------------------------------------------------
 *
 * Two different moments, from the two kinds of window the park publishes:
 *
 *   choosing an arrival    a date is greyed when NO stay of any length can
 *                          begin there. That is the `closed` windows and the
 *                          arrival-side rules, and both are known before the
 *                          guest has touched anything.
 *   choosing a departure   a date is greyed when this arrival cannot reach it.
 *                          That is the `switching` windows and the length and
 *                          departure-day rules, and none of it can be drawn
 *                          until an arrival exists.
 *
 * A third case is deliberately NOT drawn here at all: a filter that matches no
 * accommodation. A calendar cannot say that — it would go uniformly dark, and
 * a park does not become full because somebody ticked "own sanitary
 * facilities". The caller shows a message instead, and `emptyFilter` is how it
 * tells this component to stay out of the way.
 *
 * ---------------------------------------------------------------------------
 * MOBILE FIRST, AND WHERE THE TWO DIVERGE
 * ---------------------------------------------------------------------------
 *
 * One implementation, two presentations. The months are a SCROLLING list, not
 * a paged one: a thumb scrolls, and month-arrows on a phone are two taps to
 * discover a date the guest could have flicked past. Desktop lays the same list
 * out two months wide inside a popover.
 *
 * The only behavioural difference is how the panel closes. On desktop, picking
 * a departure closes it — the guest saw the whole thing and the pointer is
 * precise. On a phone the sheet keeps a sticky footer with both dates and a
 * confirm, because auto-closing under a thumb after a mis-tap is the one
 * interaction a guest cannot undo without starting again.
 */

export type Picking = 'arrival' | 'departure';

export function DateRange({
  arrival, departure, onChange,
  arrivable, departures, minDay, monthsFrom, monthCount,
  loading, emptyFilter, unknownBeyond, onNeedMonths, monthNav,
}: {
  arrival: Day | null;
  departure: Day | null;
  /** Both at once, because a change to one is usually a change to both. */
  onChange: (next: { arrival: Day | null; departure: Day | null }) => void;
  /** Dates a stay may begin on. Null while unknown. */
  arrivable: Set<Day> | null;
  /** Dates this arrival may end on. Null when there is no arrival yet. */
  departures: Set<Day> | null;
  minDay: Day;
  /** First month to draw. */
  monthsFrom: Day;
  monthCount: number;
  loading?: boolean;
  /** The filters match nothing: say so above, and do not grey the grid. */
  emptyFilter?: boolean;
  /** First day nothing is known about, so cells past it are not drawn as full. */
  unknownBeyond?: Day | null;
  /** The guest scrolled near the end of what is drawn. */
  onNeedMonths?: () => void;
  /**
   * How the guest moves between months.
   *
   * `undefined` follows the convention every major booking site shares: a
   * paging popover on a desktop, a scrolling full-screen dialog on a phone.
   *
   * `'paged'` forces paging on both, and the compat bridge asks for it. The
   * mobile half of that convention assumes the dialog can fill the SCREEN, and
   * inside an iframe it cannot — "full screen" is the frame, which is a box on
   * somebody else's page. A scrolling list in a short box is a nested scroller
   * competing with the host page for the same swipe, which is exactly what the
   * operator hit: "I can't scroll in the calendar to other months on mobile".
   *
   * So the convention holds wherever we own the viewport, and paging covers
   * the one place we do not.
   */
  monthNav?: 'paged' | 'scroll';
}) {
  const t = useT();
  const { locale } = useI18n();
  const fmt = useMemo(() => formatters(locale), [locale]);

  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [picking, setPicking] = useState<Picking>('arrival');
  const [hover, setHover] = useState<Day | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  /**
   * The first row on screen.
   *
   * REPORTED: "I can't scroll in the calendar to other months on mobile", and
   * then the better observation behind it — paging with arrows is what people
   * are used to, on a phone and on a desktop alike.
   *
   * They were right twice over. A scrolling list inside an iframe is a nested
   * scroller, which is the most fragile gesture on the mobile web: the page
   * behind it wants the same swipe. And its height is unbounded, which is what
   * produced a 420px window over 2238px of calendar, and before that a 2897px
   * frame. Paging has neither problem — one month is one month.
   */
  const [page, setPage] = useState(0);
  const scroller = useRef<HTMLDivElement | null>(null);
  /** The open panel itself, which `fitPanel` sizes against the frame. */
  const box = useRef<HTMLDivElement | null>(null);

  // Read once per open, not subscribed: a guest who rotates mid-pick has
  // already decided how they are holding the phone.
  const wide = useMemo(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(min-width: 700px)').matches
      : true
  ), [open]);

  /**
   * How long a finished range stays on screen before the sheet closes itself.
   *
   * Long enough to SEE what was chosen — the range shades, and a guest who
   * mis-tapped watches the wrong week light up and can fix it — and short
   * enough that nobody wonders whether the thing is stuck. The old calculator
   * did the same, and the operator described it from memory as "briefly shows
   * the selected dates and closes", which is the behaviour to match.
   *
   * Any further tap cancels it: the guest is still deciding, and a panel that
   * vanished mid-decision would be the worse failure.
   */
  const CLOSE_AFTER_MS = 1200;

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const close = () => { cancelClose(); setOpen(false); };

  // A pending close must not outlive the component, or it fires into nothing.
  useEffect(() => cancelClose, []);

  const months = useMemo(
    () => Array.from({ length: monthCount }, (_, i) => addMonths(startOfMonth(monthsFrom), i)),
    [monthsFrom, monthCount],
  );

  /** Close on Escape and on a click outside — a popover, not a modal. */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    const onDown = (e: MouseEvent) => {
      if (panel.current && !panel.current.contains(e.target as Node)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const start = (which: Picking) => {
    setPicking(which);
    setOpen(true);
  };

  function pick(day: Day) {
    // The guest is still choosing, so whatever close was pending is not wanted.
    cancelClose();
    if (picking === 'arrival') {
      // A new arrival always clears the departure: the old one was reachable
      // from a different date and quietly keeping it is how a guest ends up
      // enquiring about a stay they never chose.
      onChange({ arrival: day, departure: null });
      setPicking('departure');
      return;
    }
    // Before or on the arrival: the guest is not choosing a departure, they are
    // choosing a new arrival and have said so by clicking to the left of it.
    if (arrival === null || day <= arrival) {
      onChange({ arrival: day, departure: null });
      setPicking('departure');
      return;
    }
    onChange({ arrival, departure: day });
    /**
     * Desktop closes on the second click. A phone shows the finished range for
     * a moment and then closes itself.
     *
     * It used to wait for the confirm button, on the reasoning that a mis-tap
     * under a thumb is one more tap to fix rather than a reopen. That is true
     * and it is not the whole cost: a sheet that fills a phone screen and only
     * leaves when you press the right button is a sheet a guest can feel stuck
     * in — and the confirm was disabled until both dates were chosen, so
     * somebody who opened it by accident had nothing to press at all.
     *
     * The delay keeps what the confirm was protecting: the range is visible
     * before it goes, another tap cancels the close, and the fields reopen
     * showing what was chosen.
     */
    if (wide) setOpen(false);
    else closeTimer.current = setTimeout(() => setOpen(false), CLOSE_AFTER_MS);
  }

  const selectable = (day: Day): boolean => {
    if (day < minDay) return false;
    if (emptyFilter) return false;
    if (picking === 'departure' && arrival !== null) {
      // Restarting is always allowed, which is why a date left of the arrival
      // is live even though it is not a departure.
      if (day <= arrival) return arrivable === null || arrivable.has(day);
      return departures !== null && departures.has(day);
    }
    return arrivable === null || arrivable.has(day);
  };

  /**
   * The months to draw, with unusable runs folded into one line each.
   *
   * Folded only when the answer is KNOWN: never while `arrivable` is still
   * null, never when the filters matched nothing — that is a sentence above the
   * grid rather than a fact about the park — and never a month holding a date
   * the guest has already chosen or the earliest one they still may.
   */
  const rows = useMemo(
    () => calendarRows(months, {
      selectable,
      unknownBeyond: unknownBeyond ?? null,
      keep: [arrival, departure, minDay],
      disabled: !!emptyFilter || (arrivable === null && departures === null),
    }),
    // `selectable` closes over these, and is rebuilt every render by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [months, arrivable, departures, arrival, departure, minDay, picking, emptyFilter, unknownBeyond],
  );

  /**
   * How many rows a page holds, and where the page sits.
   *
   * Two months side by side on a desktop, one on a phone — the shapes every
   * date picker uses, and the reason the arrows feel like nothing new.
   */
  const paged = monthNav === 'paged' || wide;
  /**
   * A bottom sheet, or a panel in the flow under the field.
   *
   * DEFECT (reported): on the camping page the calendar "does not render under
   * the calendar field". It rendered at the bottom of the FRAME — a sheet is
   * `position: fixed`, and inside an iframe that pins it to the frame's
   * viewport, which on that page is a tall box further down somebody else's
   * article. The guest tapped a field near the top and the calendar appeared
   * past the party counters.
   *
   * A sheet is a full-screen shape and a frame has no full screen to give it.
   * So a host that asked for paging — which is a host that knows it is a frame
   * — gets the panel in normal flow, directly under the field that opened it.
   */
  const sheet = !wide && monthNav !== 'paged';
  const perPage = wide ? 2 : 1;
  const lastPage = Math.max(0, rows.length - perPage);
  const at = Math.min(page, lastPage);
  const shown = paged ? rows.slice(at, at + perPage) : rows;

  /**
   * Open on the month the guest is already in, not on the first one drawn.
   *
   * Reopening to change a departure in September, and being shown August, is a
   * page they have to leave before they can do anything.
   */
  useEffect(() => {
    if (!open) return;
    const land = departure ?? arrival ?? minDay;
    const i = rows.findIndex((r) => (r.kind === 'month'
      ? isSameMonth(r.month, land)
      : land >= r.from && land < addMonths(r.to, 1)));
    setPage(i < 0 ? 0 : Math.min(i, Math.max(0, rows.length - perPage)));
    // Only when the panel opens: paging afterwards is the guest's business.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Draw further ahead once the guest is near the end of what is loaded.
   *
   * The scroll listener this replaces fired on a gesture; a page turn is the
   * same signal and arrives one page earlier, which is enough time for the
   * fetch to land before the guest asks for the month.
   */
  useEffect(() => {
    if (paged && open && onNeedMonths && at + perPage >= rows.length - 1) onNeedMonths();
  }, [paged, open, at, perPage, rows.length, onNeedMonths]);

  /** Scrolling asks for more the same way it always did: near the end. */
  /**
   * Never taller than the box we are in, and say how tall we would like to be.
   *
   * Both halves matter and neither is enough alone; `fitPanel` carries the
   * reasoning. The sheet is excluded because it is only used where we own the
   * viewport, and because it is `position: fixed` at the bottom — its top edge
   * IS a function of its height, so capping from that top would chase itself
   * down the screen a few pixels at a time.
   *
   * Re-run on `resize` because that is what a host growing the frame looks like
   * from in here, and it is how the cap gets out of the way again.
   */
  useEffect(() => {
    const el = box.current;
    const win = el?.ownerDocument.defaultView;
    if (!open || !el || !win || sheet) return undefined;
    const apply = () => {
      // Uncapped first: `offsetHeight` under a cap is the cap, and measuring
      // that would ratchet the panel smaller on every pass.
      el.style.maxHeight = '';
      const natural = el.offsetHeight;
      const { maxHeight, wants } = fitPanel(
        el.getBoundingClientRect().top, natural, win.innerHeight, win.scrollY,
      );
      if (natural > maxHeight) el.style.maxHeight = `${maxHeight}px`;
      // Read by the compat bridge's height reporter. See `fitPanel`.
      el.dataset.pwGrow = String(wants);
    };
    apply();
    win.addEventListener('resize', apply);
    return () => win.removeEventListener('resize', apply);
    // `at` and `rows.length` because a page turn or a fetch changes the height.
  }, [open, sheet, at, rows.length, loading]);

  useEffect(() => {
    const el = scroller.current;
    if (paged || !el || !onNeedMonths) return undefined;
    const onScroll = () => {
      if (el.scrollTop + el.clientHeight > el.scrollHeight - 400) onNeedMonths();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [paged, onNeedMonths, open]);

  // The range to shade: the chosen stay, or the one the pointer is proposing.
  const end = departure ?? (picking === 'departure' && hover !== null && arrival !== null
    && hover > arrival ? hover : null);

  const field = (which: Picking, value: Day | null, label: string) => (
    <button
      type="button"
      className={`pw-dr-field${picking === which && open ? ' pw-dr-field-active' : ''}`}
      onClick={() => start(which)}
      aria-expanded={open && picking === which}
    >
      <span className="pw-dr-label">{label}</span>
      <span className={`pw-dr-value${value === null ? ' pw-dr-empty' : ''}`}>
        {value === null ? t('search.pickDate') : fmt.short(value)}
      </span>
    </button>
  );

  return (
    <div className="pw-dr" ref={panel}>
      <div className="pw-dr-fields">
        {field('arrival', arrival, t('search.arrival'))}
        <span className="pw-dr-arrow" aria-hidden="true">→</span>
        {field('departure', departure, t('search.departure'))}
        {(arrival !== null || departure !== null) && (
          <button
            type="button"
            className="pw-dr-clear"
            onClick={() => { onChange({ arrival: null, departure: null }); setPicking('arrival'); }}
          >
            {t('search.clearDates')}
          </button>
        )}
      </div>

      {open && (
        <div
          ref={box}
          className={`pw-dr-panel${wide ? ' pw-dr-wide' : sheet ? ' pw-dr-sheet' : ' pw-dr-inline'}`}
          role="dialog"
          aria-label={t(picking === 'arrival' ? 'search.pickArrival' : 'search.pickDeparture')}
        >
          <div className="pw-dr-head">
            {/*
              Paging, which is what a date picker looks like everywhere else.
              
              Disabled at the ends rather than hidden: a control that vanishes
              moves the one beside it, and a guest who has just tapped `next`
              twice should not find `prev` where `next` was.
            */}
            {paged && (
              <button
                type="button"
                className="pw-dr-page"
                aria-label={t('search.prevMonth')}
                disabled={at === 0}
                onClick={() => { cancelClose(); setPage(Math.max(0, at - perPage)); }}
              >
                <Chevron dir="left" />
              </button>
            )}
            <div className="pw-dr-prompt" aria-live="polite">
              {t(picking === 'arrival' ? 'search.pickArrival' : 'search.pickDeparture')}
            </div>
            {paged && (
              <button
                type="button"
                className="pw-dr-page"
                aria-label={t('search.nextMonth')}
                disabled={at >= lastPage}
                onClick={() => { cancelClose(); setPage(Math.min(lastPage, at + perPage)); }}
              >
                <Chevron dir="right" />
              </button>
            )}
            {/*
              A way out, on the shape that needs one.
              
              A sheet fills a phone screen, so "tap outside" has no outside to
              tap, and the confirm below is disabled until both dates are
              chosen — a guest who opened this by accident had nothing to press.
              The desktop popover is a popover: clicking the page behind it
              closes it, and an extra control there is furniture.
            */}
            {!wide && (
              <button
                type="button"
                className="pw-dr-close"
                aria-label={t('search.closeDates')}
                onClick={close}
              >
                &times;
              </button>
            )}
          </div>

          {emptyFilter && <p className="pw-dr-note" role="status">{t('search.filterEmpty')}</p>}

          {loading && (
            <p className="pw-busy">
              <span className="pw-spinner" aria-hidden="true" />
              {t('search.loadingDates')}
            </p>
          )}

          <div className={`pw-dr-months${paged ? '' : ' pw-dr-scrolls'}`} ref={scroller}>
            {shown.map((row) => (row.kind === 'month' ? (
              <Month
                key={row.month}
                month={row.month}
                minDay={minDay}
                arrival={arrival}
                end={end}
                picking={picking}
                selectable={selectable}
                unknownBeyond={unknownBeyond ?? null}
                onPick={pick}
                onHover={setHover}
                fmt={fmt}
              />
            ) : (
              /*
                A run of months nothing can be picked in, as one line.
                
                The park is shut from October to late March, and a guest used to
                scroll six grids of uniformly grey squares to get past it — which
                reads as a broken calendar rather than as a closed park. Deleting
                them silently would trade that for a different confusion, so the
                line says which months and why.
              */
              <p className="pw-dr-gap" key={`gap-${row.from}`}>
                <span className="pw-dr-gap-when">
                  {row.count === 1 ? fmt.month(row.from)
                    : t('search.gapRange', { from: fmt.month(row.from), to: fmt.month(row.to) })}
                </span>
                <span className="pw-dr-gap-why">
                  {picking === 'departure' && arrival !== null
                    ? t('search.gapNoDeparture')
                    : t('search.gapClosed')}
                </span>
              </p>
            )))}
          </div>

          {!wide && (
            <div className="pw-dr-foot">
              <span className="pw-dr-summary">
                {arrival === null ? t('search.pickArrival')
                  : departure === null ? t('search.pickDeparture')
                    : `${fmt.short(arrival)} → ${fmt.short(departure)}`}
              </span>
              <button
                type="button"
                className="pw-btn pw-btn-primary"
                disabled={arrival === null || departure === null}
                onClick={() => setOpen(false)}
              >
                {t('search.datesDone')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The paging arrow, drawn rather than typed.
 *
 * `&lsaquo;` and `&rsaquo;` are single guillemets: punctuation, sized and
 * placed by whatever font the park's page happens to load, and in the operator's
 * phone that put a small mark against one side of a tap-sized box. A path has
 * the size and the centre we asked for, on every font.
 *
 * `currentColor` and no explicit size, so the disabled state and the tap target
 * stay the button's business rather than being restated here.
 */
function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg
      className="pw-chevron"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d={dir === 'left' ? 'M15 4 L7 12 L15 20' : 'M9 4 L17 12 L9 20'}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Month({
  month, minDay, arrival, end, picking, selectable, unknownBeyond, onPick, onHover, fmt,
}: {
  month: Day;
  minDay: Day;
  arrival: Day | null;
  end: Day | null;
  picking: Picking;
  selectable: (d: Day) => boolean;
  unknownBeyond: Day | null;
  onPick: (d: Day) => void;
  onHover: (d: Day | null) => void;
  fmt: ReturnType<typeof formatters>;
}) {
  const t = useT();
  const grid = useMemo(() => monthGrid(month), [month]);

  return (
    <section className="pw-dr-month">
      <h4 className="pw-dr-monthName">{fmt.month(month)}</h4>
      <div className="pw-cal-grid">
        {/* Decoration: every cell announces its own full date, so column heads
            repeated to a screen reader would be noise. */}
        {[1, 2, 3, 4, 5, 6, 0].map((wd) => (
          <div key={wd} className="pw-cal-wd" aria-hidden="true">{fmt.weekdayShort(wd)}</div>
        ))}
        {grid.map((day) => {
          if (!isSameMonth(day, month)) {
            return <div key={day} className="pw-day pw-day-out" aria-hidden="true" />;
          }
          const number = new Date(day * 86_400_000).getUTCDate();
          const full = fmt.full(day);
          const past = day < minDay;
          // Past the fetched window: not drawn as unavailable, because nothing
          // is known about it and grey would be a claim.
          const unknown = unknownBeyond !== null && day >= unknownBeyond;
          const free = !past && !unknown && selectable(day);
          const isArrival = arrival === day;
          const isEnd = end === day;
          const inRange = arrival !== null && end !== null && day > arrival && day < end;

          if (past || unknown) {
            return (
              <div key={day} className={`pw-day ${past ? 'pw-day-past' : 'pw-day-unknown'}`}>
                <span aria-hidden="true">{number}</span>
                <span className="pw-sr">{past ? t('search.dayPast', { date: full }) : full}</span>
              </div>
            );
          }

          if (!free) {
            // Two different refusals, and the hatch belongs to only one of them.
            //
            //   choosing an arrival    nothing can begin here. The park is shut
            //                          or full, and the hatch says so.
            //   choosing a departure   the park is not full at all — this stay
            //                          cannot REACH the date. From 27 August a
            //                          21-night cap puts everything past 17
            //                          September out of range, and hatching it
            //                          reads as "closed from mid-September",
            //                          which is a claim about the park rather
            //                          than about this stay.
            const why = picking === 'arrival' ? 'pw-day-full' : 'pw-day-unreachable';
            return (
              <div key={day} className={`pw-day ${why}${inRange ? ' pw-day-inStay' : ''}`}>
                <span aria-hidden="true">{number}</span>
                <span className="pw-sr">
                  {t(picking === 'arrival' ? 'search.dayNoArrival' : 'search.dayNoDeparture',
                    { date: full })}
                </span>
              </div>
            );
          }

          const classes = ['pw-day', 'pw-day-free'];
          if (isArrival) classes.push('pw-day-start');
          if (isEnd) classes.push('pw-day-end');
          if (inRange) classes.push('pw-day-inStay');

          return (
            <button
              key={day}
              type="button"
              className={classes.join(' ')}
              aria-pressed={isArrival || isEnd}
              onClick={() => onPick(day)}
              onMouseEnter={() => onHover(day)}
              onFocus={() => onHover(day)}
            >
              <span aria-hidden="true">{number}</span>
              <span className="pw-day-dot" aria-hidden="true" />
              <span className="pw-sr">
                {t(picking === 'arrival' ? 'search.dayArrival' : 'search.dayDeparture',
                  { date: full })}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
